/**
 * Data Fetch Table Component
 *
 * Table displaying data fetch requests with:
 * - Unified search across fields
 * - Pagination
 * - Account details action
 */

import React, { useState, useMemo } from 'react';
import { Table, Button, SearchBar } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import type { DataFetchTableProps, DataFetchRequest } from '../../../types/data-fetch-request';

/**
 * Filter data fetch requests based on search query and domain
 */
const filterDataFetchRequests = (
  requests: DataFetchRequest[],
  searchQuery: string,
  searchDomain: string
): DataFetchRequest[] => {
  if (!searchQuery.trim()) {
    return requests;
  }

  const query = searchQuery.toLowerCase();

  return requests.filter((request) => {
    switch (searchDomain) {
      case 'vua':
        return request.customer_vua.toLowerCase().includes(query);
      case 'consent':
        return request.consent_id.toLowerCase().includes(query);
      case 'session':
        return request.session_id.toLowerCase().includes(query);
      case 'transaction':
        return request.txn_id.toLowerCase().includes(query);
      case 'aa':
        return request.notifier_id.toLowerCase().includes(query);
      case 'all':
      default:
        // Search across all fields
        return (
          request.customer_vua.toLowerCase().includes(query) ||
          request.consent_id.toLowerCase().includes(query) ||
          request.session_id.toLowerCase().includes(query) ||
          request.txn_id.toLowerCase().includes(query) ||
          request.notifier_id.toLowerCase().includes(query) ||
          request.createdAt.toLowerCase().includes(query) ||
          request.fi_data_range_from.toLowerCase().includes(query) ||
          request.fi_data_range_to.toLowerCase().includes(query)
        );
    }
  });
};

export const DataFetchTable: React.FC<DataFetchTableProps> = ({
  data,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onViewAccountDetails,
  isLoading,
  hasAccountDetailsPermission,
}) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDomain, setSearchDomain] = useState('all');

  // Search bar domains
  const domains = [
    { value: 'all', label: 'All Fields' },
    { value: 'vua', label: 'Customer VUA' },
    { value: 'consent', label: 'Consent ID' },
    { value: 'session', label: 'Session ID' },
    { value: 'transaction', label: 'Transaction ID' },
    { value: 'aa', label: 'AA ID' },
  ];

  // Apply client-side filtering
  const filteredData = useMemo(() => {
    return filterDataFetchRequests(data, searchQuery, searchDomain);
  }, [data, searchQuery, searchDomain]);

  // Define columns
  const columns = useMemo<TableColumn<DataFetchRequest>[]>(() => {
    const cols: TableColumn<DataFetchRequest>[] = [];

    // Account Details column (conditional)
    if (hasAccountDetailsPermission) {
      cols.push({
        key: 'actions',
        header: 'Customer Accounts',
        width: 180,
        locked: true,
        render: (_value, row) => (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onViewAccountDetails(row)}
          >
            Account Details
          </Button>
        ),
      });
    }

    // Data columns
    cols.push(
      {
        key: 'customer_vua',
        header: 'Customer VUA',
        width: 200,
        render: (_value, row) => (
          <span className="text-sm">{row.customer_vua}</span>
        ),
      },
      {
        key: 'consent_id',
        header: 'Consent Id',
        width: 300,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.consent_id}</span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Fetch Date',
        width: 200,
        render: (_value, row) => (
          <span className="text-sm">{row.createdAt}</span>
        ),
      },
      {
        key: 'fi_data_range_from',
        header: 'FI Data Range From',
        width: 180,
        render: (_value, row) => (
          <span className="text-sm">{row.fi_data_range_from}</span>
        ),
      },
      {
        key: 'fi_data_range_to',
        header: 'FI Data Range To',
        width: 180,
        render: (_value, row) => (
          <span className="text-sm">{row.fi_data_range_to}</span>
        ),
      },
      {
        key: 'session_id',
        header: 'Session Id',
        width: 400,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.session_id}</span>
        ),
      },
      {
        key: 'notifier_id',
        header: 'AA Id',
        width: 150,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.notifier_id}</span>
        ),
      },
      {
        key: 'txn_id',
        header: 'Transaction Id',
        width: 300,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.txn_id}</span>
        ),
      }
    );

    return cols;
  }, [hasAccountDetailsPermission, onViewAccountDetails]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-lg bg-white">
      {/* Header with Search and Actions */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1">
          <SearchBar
            variant="with-dropdown"
            domains={domains}
            value={searchQuery}
            onChange={setSearchQuery}
            selectedDomain={searchDomain}
            onDomainChange={setSearchDomain}
            placeholder="Search data fetch requests..."
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Table
          className="flex-1 min-h-0 shadow-sm rounded-xl"
          columns={columns}
          data={filteredData}
          loading={isLoading}
          columnManager={{
            enabled: true,
            triggerLabel: 'Manage columns',
            allowHiding: true,
          }}
          pagination={{
            currentPage,
            pageSize,
            totalItems,
            onPageChange,
            onPageSizeChange,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
        />
      </div>
    </div>
  );
};
